<template>
  <Modal
    :value="visible"
    @input="updateVisible"
    :contentClass="'py-4 px-6'"
    :show-close-button="false"
  >
    <template v-slot:header>
      <Typography variant="h-5"> Add Your Home Address </Typography>
    </template>
    <div class="text-left mt-4">
      <Typography variant="body-1" as="p">
        We need your home address before you can redeem rewards from the catalog. This ensures items
        are shipped to the right place.
      </Typography>

      <v-form ref="form" v-model="valid" lazy-validation>
        <v-text-field
          v-model="form.address1"
          label="Street Address*"
          required
          :rules="[(v) => !!v || 'Street Address is required']"
          outlined
        ></v-text-field>

        <v-text-field
          v-model="form.address2"
          label="Apartment, suite, etc. (optional)"
          outlined
        ></v-text-field>

        <v-text-field
          v-model="form.city"
          label="City*"
          required
          :rules="[(v) => !!v || 'City is required']"
          outlined
        ></v-text-field>

        <v-select
          v-if="!form.country || form.country === 'US'"
          v-model="form.state"
          :items="states"
          label="State / Province*"
          item-text="name"
          item-value="abbreviation"
          required
          :rules="[(v) => !!v || 'State / Province is required']"
          outlined
          :menu-props="{ bottom: true, offsetY: true }"
        ></v-select>
        <v-text-field
          v-else
          v-model="form.state"
          label="State / Province*"
          required
          :rules="[(v) => !!v || 'State / Province is required']"
          outlined
        ></v-text-field>

        <v-text-field
          v-model="form.postalCode"
          label="Zip / Postal Code*"
          required
          :rules="[(v) => !!v || 'Zip / Postal Code is required']"
          outlined
        ></v-text-field>

        <v-select
          v-model="form.country"
          :items="countries"
          label="Country*"
          item-text="name"
          item-value="code"
          required
          :rules="[(v) => !!v || 'Country is required']"
          outlined
          :menu-props="{ bottom: true, offsetY: true }"
        ></v-select>

        <div class="d-flex justify-end flex-row" style="gap: 16px">
          <Button text color="brandPrimary" @click="close"> CANCEL </Button>
          <Button color="brandPrimary" :loading="loading" :disabled="!isFormValid" @click="save">
            SAVE & CONTINUE
          </Button>
        </div>
      </v-form>
    </div>
  </Modal>
</template>

<script>
import Modal from "@/components/generic/Modal.vue";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import { states, countries } from "@/shared_data/data.js";
import { mapState } from "vuex";
import UserService from "../../../services/UserService";

export default {
  name: "AddressFormModal",
  components: {
    Modal,
    Typography,
    Button,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      valid: false,
      loading: false,
      states: states,
      countries: countries,
      form: {
        address1: "",
        address2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "US",
      },
    };
  },
  computed: {
    ...mapState(["userProfile", "magicLinkToken"]),
    isFormValid() {
      return (
        !!this.form.address1 &&
        !!this.form.city &&
        !!this.form.state &&
        !!this.form.postalCode &&
        !!this.form.country
      );
    },
  },
  watch: {
    visible(val) {
      if (val && this.userProfile) {
        this.form.address1 = this.userProfile.homeAddress || "";
        this.form.address2 = this.userProfile.homeAddress2 || "";
        this.form.city = this.userProfile.homeCity || "";
        this.form.state = this.userProfile.homeState || "";
        this.form.postalCode = this.userProfile.homePostalCode || "";
        this.form.country = this.userProfile.homeCountry || "US";
      }
    },
  },
  methods: {
    updateVisible(val) {
      this.$emit("update:visible", val);
    },
    close() {
      this.updateVisible(false);
    },
    async save() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      try {
        const body = {
          homeAddress: this.form.address1,
          homeAddress2: this.form.address2,
          homeCity: this.form.city,
          homeState: this.form.state,
          homePostalCode: this.form.postalCode,
          homeCountry: this.form.country,
        };

        const response = await UserService.updateUser(
          this.userProfile.userId,
          body,
          this.magicLinkToken
        );

        this.$emit("saved", response);
        this.close();
      } catch (error) {
        console.error("Error updating address:", error);
        this.$store.dispatch("showError", {
          message: "Failed to save address. Please try again later.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.v-btn {
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 1px;
}
</style>
